import React, { useState, useEffect } from 'react';
import TopNav from './TopNav';
import LeftNav from './LeftNav';
import { useOrgBrand } from '../context/OrgBrandContext';
import '../styles/Layout.css';

const Layout = ({
  user,
  children,
  contextMenuContent = null,
  activeSection = 'dashboard',
  onSectionChange,
  onAgencyChange,
  onOrganizationChange,
  onBrandChange,
  onLogout,
  onOpenWelcome,
  onOpenOnboarding,
  activeAgency,
  orgRefreshTrigger,
  isProxyMode = false,
  showOrgSelector = true
}) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    // Initialize from localStorage if available
    // Use different keys for admin and proxied user to keep states independent
    const storageKey = isProxyMode ? 'proxiedSidebarCollapsed' : 'adminSidebarCollapsed';
    const saved = localStorage.getItem(storageKey);
    return saved ? JSON.parse(saved) : false;
  });
  // Off-canvas nav drawer state, only relevant below the mobile breakpoint
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => window.innerWidth <= 768);
  const { selectedBrand, selectedOrganization } = useOrgBrand();

  const handleToggleMobileNav = () => {
    setIsMobileNavOpen(prev => !prev);
  };

  const handleCloseMobileNav = () => {
    setIsMobileNavOpen(false);
  };

  // Close the mobile drawer whenever navigation actually happens
  const handleSectionChange = (section, extraParams) => {
    setIsMobileNavOpen(false);
    if (onSectionChange) {
      onSectionChange(section, extraParams);
    }
  };

  // Track the mobile breakpoint so the nav renders fully expanded (not icon-only
  // collapsed) when shown as an off-canvas drawer, and auto-close the drawer
  // if the window is resized back past the breakpoint
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobileViewport(mobile);
      if (!mobile && isMobileNavOpen) {
        setIsMobileNavOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileNavOpen]);

  // Lock background scroll while the mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = isMobileNavOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileNavOpen]);

  // FIX: The brand bar color logic
  // Only use brand color when showOrgSelector is true AND org is selected (when context is actually shown)
  // This prevents the outer admin layout from picking up orphaned brand colors or proxied user's brand color
  const brandMenuColor = showOrgSelector && selectedOrganization && selectedBrand?.menu_color
    ? selectedBrand.menu_color
    : 'var(--gray-500)';

  // Set CSS variable on document root to avoid inline styles
  useEffect(() => {
    document.documentElement.style.setProperty('--brand-menu-color', brandMenuColor);
  }, [brandMenuColor]);

  // Listen for collapse event from proxy user selection (admin nav only)
  useEffect(() => {
    if (!isProxyMode) {
      const handleCollapseEvent = () => {
        setIsSidebarCollapsed(true);
      };
      window.addEventListener('collapseAdminNav', handleCollapseEvent);
      return () => window.removeEventListener('collapseAdminNav', handleCollapseEvent);
    }
  }, [isProxyMode]);

  const handleToggleSidebar = () => {
    setIsSidebarCollapsed(prev => {
      const newValue = !prev;
      // Save to localStorage using different keys for admin and proxied user
      const storageKey = isProxyMode ? 'proxiedSidebarCollapsed' : 'adminSidebarCollapsed';
      localStorage.setItem(storageKey, JSON.stringify(newValue));
      return newValue;
    });
  };

  return (
    <div className={`app-layout ${isProxyMode ? 'proxy-mode' : ''}`}>
      <LeftNav
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        isCollapsed={isMobileViewport ? false : isSidebarCollapsed}
        onToggleCollapse={handleToggleSidebar}
        isMobileNavOpen={isMobileNavOpen}
        user={user}
        onLogout={onLogout}
        onAgencyChange={onAgencyChange}
        onOpenWelcome={onOpenWelcome}
        onOpenOnboarding={onOpenOnboarding}
        isProxyMode={isProxyMode}
      />

      {!isProxyMode && isMobileNavOpen && (
        <div className="mobile-nav-backdrop" onClick={handleCloseMobileNav} />
      )}

      <div className={`main-layout ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <TopNav
          user={user}
          activeSection={activeSection}
          onToggleMobileNav={isProxyMode ? undefined : handleToggleMobileNav}
        />

        <div className={`main-screen main-screen--${activeSection}`}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;
